import { ArrowDropDownOutlined, AttachFile, CloseOutlined, FormatAlignCenterOutlined, FormatAlignJustifyOutlined, FormatAlignLeftOutlined, FormatAlignRightOutlined, FormatClearOutlined, FormatItalicOutlined, FormatListBulletedOutlined, FormatListNumberedOutlined, FormatSizeOutlined } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { IEmailAttachFile } from "./Models";
import { getEmailAttachFiles, removeEmailAttachFiles } from "./DataServices/ApartmentsListingsDataServices";
import Loading from "./Loading";
import MessageBox from "./MessageBox";

interface TextEditorProps {
    content: string,
    templateType?: string,
    height?: string,
    emailId?: string;
    docs: IEmailAttachFile[],
    setDocs: React.Dispatch<React.SetStateAction<IEmailAttachFile[]>>,
    setContent: (content: string) => void
}

function TextEditor({ content, setContent, templateType, height = "16rem", docs, setDocs, emailId }: TextEditorProps) {
    const editorRef = useRef<HTMLDivElement>(null);
    const [isAlignBarOpen, setIsAlignBarOPen] = useState<boolean>(false);
    const [isFontSizeBarOpen, setIsFontSizeBarOPen] = useState<boolean>(false);
    const [docsLoading, setDocsLoading] = useState<boolean>(false);
    const [deleteDoc, setDeleteDoc] = useState<string>("");

    const handleCommand = (command: string) => {
        document.execCommand(command);
        if (editorRef.current) {
            editorRef.current.focus();
        }
    };

    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== content) {
            editorRef.current.innerHTML = content;
        }
    }, [content]);

    useEffect(() => {
        setDocs([])
        const GetEmailAttachFiles = async (id: string) => {
            setDocsLoading(true)
            await getEmailAttachFiles(id).then(data => {
                setDocs(data);
            })
            setDocsLoading(false);
        }
        emailId && GetEmailAttachFiles(emailId);
    }, [emailId])

    const handleHeadingCommand = (command: string, size: number) => {
        document.execCommand('formatBlock', false, command);
        document.execCommand('fontSize', false, size.toString());
        if (editorRef.current) {
            editorRef.current.focus();
        }
    };

    const handleInput = () => {
        if (editorRef.current) {
            const content = editorRef.current.innerHTML;
            setContent(content);
        }
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const fileReaders: Promise<IEmailAttachFile>[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            const readerPromise = new Promise<IEmailAttachFile>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    if (e.target?.result && typeof e.target.result === 'string') {
                        resolve({
                            id: "00000000-0000-0000-0000-000000000000",
                            emailId: emailId ?? "",
                            title: file.name,
                            file: e.target.result,
                        });
                    } else {
                        reject("Failed to read file");
                    }
                };
                reader.onerror = () => reject("File reading error");
                reader.readAsDataURL(file);
            });

            fileReaders.push(readerPromise);
        }

        Promise.all(fileReaders)
            .then(newDocs => {
                setDocs(prev => [...prev, ...newDocs]);
            })
            .catch(error => {
                console.error("File upload error:", error);
            });
    };


    const removeFile = async () => {
        var temp = deleteDoc;
        setDeleteDoc("")
        setDocs(prev => prev.filter((_) => _.id !== temp));
        if (deleteDoc.includes('-'))
            await removeEmailAttachFiles(temp);
        
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            setTimeout(() => {
                if (editorRef.current) {
                    const content = editorRef.current.innerHTML;
                    setContent(content);
                }
            }, 0);
        }
    };

    const isActive = (command: string) => {
        return document.queryCommandState(command);
    };

    return (
        <div className="w-full flex items-center justify-start relative">
            <div className={`flex w-full flex-col h-\[${height}\]`}>
                <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    className={`w-full min-h-[16rem] p-3 flex-grow overflow-y-auto text-sm text-gray-700  border-none focus:outline-none focus:ring-0 prose max-w-none`}
                ></div>
                <div className="flex flex-wrap min-h-fit items-center justify-start gap-3 mt-4 p-2">
                    {!docsLoading && docs?.map((item, i) => {
                        return (
                            item.title.length > 0 && <div key={i} className="flex bg-gray-100 w-fit items-center justify-between gap-4 rounded border shadow px-2">
                                <div className="text-xs text-blue-600 font-medium">{item.title}</div>
                                <div className="pb-1 cursor-pointer" onClick={() => setDeleteDoc(item.id)}><CloseOutlined fontSize="inherit" /></div>
                            </div>
                        )
                    })}
                    {docsLoading && <Loading />}
                </div>
            </div>

            <div className="flex bg-white items-center gap-2 absolute -bottom-14 left-1 shadow-md shadow-gray-200 border-[0.5px] p-2 rounded">

                <button className={"flex items-center justify-center font-bold rounded w-8 h-8 p-2 hover:bg-gray-200 " + (isActive('bold') && 'bg-gray-200')} onClick={() => handleCommand('bold')} disabled={false}><b>B</b></button>
                <button className={"flex items-center justify-center font-bold italic rounded w-8 h-8 p-2 hover:bg-gray-200 " + (isActive('italic') && 'bg-gray-200')} onClick={() => handleCommand('italic')}><FormatItalicOutlined fontSize="small" /></button>
                <button className={"flex items-center justify-center font-bold rounded w-8 h-8 p-2 underline hover:bg-gray-200 " + (isActive('underline') && 'bg-gray-200')} onClick={() => handleCommand('underline')}>U</button>
                <button className={"flex items-center justify-center rounded w-8 h-8 p-2 hover:bg-gray-200 " + (isActive('removeFormat') && 'bg-gray-200')} onClick={() => handleCommand('removeFormat')}><FormatClearOutlined fontSize="small" /></button>

                <label className={"flex items-center justify-center cursor-pointer rounded w-8 h-8 p-2 hover:bg-gray-200 " + (isActive('removeFormat') && 'bg-gray-200')}>
                    <input type="file" onChange={handleImageUpload} className="hidden" multiple />
                    <AttachFile fontSize="small" />
                </label>

                <button className={"flex items-center justify-center relative rounded w-12 h-8 p-2 hover:bg-gray-200 " + (isAlignBarOpen && 'bg-gray-200')} onClick={() => setIsAlignBarOPen(!isAlignBarOpen)}>
                    <FormatAlignLeftOutlined fontSize="small" />
                    <ArrowDropDownOutlined fontSize="small" />
                    <div className={"flex flex-col items-center justify-center absolute border shadow-md w-full bg-white -top-32 right-0 " + (!isAlignBarOpen && 'hidden')}>
                        <button className={"flex items-center justify-center w-full h-8 p-2 hover:bg-gray-200 " + (isActive('justifyLeft') && 'bg-gray-200')} onClick={() => handleCommand('justifyLeft')}> <FormatAlignLeftOutlined fontSize="small" /> </button>
                        <button className={"flex items-center justify-center w-full h-8 p-2 hover:bg-gray-200 " + (isActive('justifyCenter') && 'bg-gray-200')} onClick={() => handleCommand('justifyCenter')}><FormatAlignCenterOutlined fontSize="small" /></button>
                        <button className={"flex items-center justify-center w-full h-8 p-2 hover:bg-gray-200 " + (isActive('justifyRight') && 'bg-gray-200')} onClick={() => handleCommand('justifyRight')}><FormatAlignRightOutlined fontSize="small" /></button>
                        <button className={"flex items-center justify-center w-full h-8 p-2 hover:bg-gray-200 " + (isActive('justifyFull') && 'bg-gray-200')} onClick={() => handleCommand('justifyFull')}><FormatAlignJustifyOutlined fontSize="small" /></button>
                    </div>
                </button>

                <button className={"flex items-center justify-center rounded w-8 h-8 p-2 hover:bg-gray-200 " + (isActive('insertOrderedList') && 'bg-gray-200')} onClick={() => handleCommand('insertOrderedList')}><FormatListNumberedOutlined fontSize="small" /></button>
                <button className={"flex items-center justify-center rounded w-8 h-8 p-2 hover:bg-gray-200 " + (isActive('insertUnorderedList') && 'bg-gray-200')} onClick={() => handleCommand('insertUnorderedList')}><FormatListBulletedOutlined fontSize="small" /></button>

                <button className={"flex items-center justify-center relative rounded w-max h-8 p-2 hover:bg-gray-200 " + (isFontSizeBarOpen && 'bg-gray-200')} onClick={() => setIsFontSizeBarOPen(!isFontSizeBarOpen)}>

                    <FormatSizeOutlined fontSize="small" />
                    <ArrowDropDownOutlined fontSize="small" />
                    <div className={"flex flex-col items-center justify-center fixed border shadow-md bg-white bottom-14 " + (!isFontSizeBarOpen && 'hidden')}>
                        <div className="flex items-center justify-center w-full text-sm h-8 p-2 hover:bg-gray-200" onClick={() => handleHeadingCommand('H6', 1)}>
                            Small
                        </div>
                        <div className="flex items-center justify-center w-full h-8 p-2 hover:bg-gray-200" onClick={() => handleHeadingCommand('H6', 2)}>
                            Normal
                        </div>
                        <div className="flex items-center text-lg font-medium justify-center w-full h-8 p-2 hover:bg-gray-200" onClick={() => handleHeadingCommand('H2', 4)}>
                            Medium
                        </div>
                        <div className="flex items-center text-2xl font-bold justify-center w-full h-8 p-2 hover:bg-gray-200" onClick={() => handleHeadingCommand('H1', 6)}>
                            Large
                        </div>
                    </div>
                </button>
            </div>
            <MessageBox
                title="Delete Attach File"
                message="Are you sure you want to delete the attached file?"
                isOpen={deleteDoc.length > 0}
                setIsOpen={() => setDeleteDoc("")}
                okButtonText="Ok"
                cancelButtonText="Cancel"
                okButtonFunction={removeFile}
                cancelButtonFunction={() => setDeleteDoc("")}
                okButtonStyle={{
                    backgroundColor: "rgb(255, 0, 0)",
                }}
                iconColor={{ color: "rgb(255, 0, 0)" }}
            />
        </div>
    )
}

export default TextEditor
